import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, Trophy, ArrowRight, Zap } from "lucide-react";
import { WHATSAPP_SALES_URL } from "@/lib/sales";

const COUPON_COLOR_HEX = {
  blue: "#3b82f6",
  red: "#ef4444",
  green: "#16a34a",
  purple: "#8b5cf6",
  yellow: "#eab308",
  orange: "#f97316",
  brown: "#78350f",
  pink: "#ec4899",
  black: "#111827",
  white: "#f8fafc",
};

const WeeklyPromoCard = () => {
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/weekly-promo`).then((r) => setPromo(r.data)).catch(() => setPromo(null));
  }, []);

  if (!promo || promo.enabled === false) return null;

  const couponHex = COUPON_COLOR_HEX[(promo.coupon_color || "blue").toLowerCase()] || COUPON_COLOR_HEX.blue;

  return (
    <section className="py-12 px-4 bg-white" data-testid="weekly-promo-section">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden shadow-2xl border-2 border-green-500 bg-gradient-to-br from-green-700 via-emerald-700 to-green-800 text-white">
          {/* Decorative bg */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,white,transparent_60%)]" />
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-400/20 blur-3xl" />

          <div className="relative p-6 sm:p-10">
            {/* Week + coupon color badges (auto-synced with current_coupon) */}
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="promo-header-badges">
              {promo.week_number != null && (
                <Badge className="bg-white/15 text-white font-bold border border-white/20 backdrop-blur-sm" data-testid="promo-week-badge">
                  <Zap className="h-3 w-3 mr-1" /> Week {promo.week_number}
                </Badge>
              )}
              {promo.coupon_color && (
                <Badge
                  className="text-white font-bold border-2 border-white/40"
                  style={{ backgroundColor: couponHex }}
                  data-testid="promo-coupon-badge"
                >
                  {String(promo.coupon_color).toUpperCase()} COUPON
                </Badge>
              )}
              {promo.season && (
                <Badge className="bg-white/10 text-white border border-white/20 backdrop-blur-sm">
                  {promo.season} SEASON
                </Badge>
              )}
            </div>

            {/* Eyebrow */}
            {promo.eyebrow && (
              <p className="text-xs sm:text-sm font-bold tracking-[0.2em] text-emerald-100 mb-3 uppercase" data-testid="promo-eyebrow">
                {promo.eyebrow}
              </p>
            )}

            {/* BIG BOLD HEADLINE — the featured picks */}
            <h2
              className="text-5xl sm:text-6xl md:text-7xl font-black leading-none tracking-tight mb-3 text-white drop-shadow-lg"
              style={{ fontFamily: "Impact, 'Bebas Neue', system-ui, sans-serif", letterSpacing: "0.02em" }}
              data-testid="promo-headline"
            >
              {promo.headline}
            </h2>

            {/* Sub-headline (still large, less than main) */}
            {promo.sub_headline && (
              <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-amber-300 mb-6" data-testid="promo-sub-headline">
                {promo.sub_headline}
              </p>
            )}

            {/* Price + CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-5">
              {promo.price_text && (
                <div className="bg-white rounded-2xl px-6 py-3 shadow-lg" data-testid="promo-price">
                  <p className="text-3xl sm:text-4xl font-black text-green-700">{promo.price_text}</p>
                </div>
              )}
              {promo.cta_text && (
                <Button
                  onClick={() => window.open(/^https?:\/\//i.test(promo.cta_link || "") ? promo.cta_link : WHATSAPP_SALES_URL, "_blank", "noopener,noreferrer")}
                  className="bg-amber-400 hover:bg-amber-500 text-gray-900 rounded-full font-black h-14 px-8 text-base shadow-xl uppercase tracking-wide"
                  data-testid="promo-cta-btn"
                >
                  <Trophy className="h-5 w-5 mr-2" /> {promo.cta_text} <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              )}
            </div>

            {/* Contact line — call icon + big phone */}
            {promo.phone_number && (
              <a
                href={`tel:+234${(promo.phone_number || "").replace(/^0/, "")}`}
                className="inline-flex items-center gap-3 bg-white/15 hover:bg-white/25 border border-white/30 backdrop-blur-sm rounded-full py-3 px-5 transition-colors"
                data-testid="promo-phone-link"
              >
                <span className="bg-white text-green-700 rounded-full p-2">
                  <Phone className="h-5 w-5" />
                </span>
                <span>
                  <span className="text-xs text-emerald-100 block">{promo.contact_label || "Call"}</span>
                  <span className="text-lg sm:text-xl font-black text-white tracking-wider" data-testid="promo-phone-number">{promo.phone_number}</span>
                </span>
              </a>
            )}

            {/* Optional note */}
            {promo.note && (
              <p className="mt-5 text-sm text-emerald-100/90 italic" data-testid="promo-note">{promo.note}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WeeklyPromoCard;
